import { type ViolationBehaviorHistory, type ViolationBehaviorRule } from '@aysar/contracts';
import { useMemo } from 'react';

import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { formatNumber } from '@/shared/utils/format-number/format-number.util';

import { type UseViolationBehaviorHistoryLogic } from './ViolationBehaviorHistory.types';

/**
 * The member's pattern history, ready to draw.
 *
 * The contract already guarantees one entry per rule in declaration order and
 * disjoint outcome buckets, so this does no reshaping — it localizes the
 * numbers, marks the case's own rule, and answers the one question a table
 * cannot: is this the member's first pattern flag ever? That is exactly one
 * counted flag across every rule, and it must be the case being reviewed —
 * which the detail's own read guarantees, because the flag is a row in it.
 */
export function useViolationBehaviorHistoryLogic(
  history: ViolationBehaviorHistory,
  currentRule: ViolationBehaviorRule,
): UseViolationBehaviorHistoryLogic {
  const { locale } = useDirection();

  return useMemo<UseViolationBehaviorHistoryLogic>(() => {
    const counted = history.map(
      (entry) => entry.pending + entry.enforced + entry.dismissed + entry.overturned,
    );

    const rows = history.map((entry, index) => {
      const flags = counted[index] ?? 0;

      return {
        rule: entry.rule,
        isCurrent: entry.rule === currentRule,
        isEmpty: flags === 0,
        flags: formatNumber(flags, locale),
        pending: formatNumber(entry.pending, locale),
        enforced: formatNumber(entry.enforced, locale),
        dismissed: formatNumber(entry.dismissed, locale),
        overturned: formatNumber(entry.overturned, locale),
        lastDetectedAt: entry.lastDetectedAt,
      };
    });

    return {
      rows,
      isFirstPattern: counted.reduce((total, flags) => total + flags, 0) === 1,
    };
  }, [history, currentRule, locale]);
}
